// apps/admin/src/features/users/AdminUserSessionsSection.tsx
//
// Phase 8.2 Wave 7 Task 50 — Sessions section of the Admin User Detail.
//
// "Đăng xuất tất cả phiên" button → ConfirmDialog → POST /sessions/revoke
// with scope='all'. Success emits toast "Đã đăng xuất N phiên" and
// invalidates the activity feed so the audit row appears immediately.
//
// vg-binding: PLAN:task-50
// vg-binding: P8.D-380  — revoke produces an audit entry visible in
//                          Activity tab after refetch
// vg-binding: P8.D-475  — self-action guard: hide the revoke button when
//                          viewing own account (parent already shows banner)

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  useToast,
} from '@printway/ui';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';

import { useTranslation } from '../../i18n/useTranslation.js';
import {
  getAdminUserSessions,
  revokeAdminUserSessions,
  type AdminUserApiError,
  type AdminUserSession,
  type RevokeAdminUserSessionsResponse,
} from '../../api/admin-users.api.js';

interface AdminUserSessionsSectionProps {
  userId: string;
  /** True iff the page is being viewed by the user themselves. */
  isSelf: boolean;
  /** Last login ISO timestamp (display only). */
  lastLoginAt: string | null;
  /** Last login IP (display only). */
  lastLoginIp: string | null;
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined);
  } catch {
    return iso;
  }
}

export function AdminUserSessionsSection({
  userId,
  isSelf,
  lastLoginAt,
  lastLoginIp,
}: AdminUserSessionsSectionProps): JSX.Element {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('users');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const sessionsQuery = useQuery<AdminUserSession[], AdminUserApiError | Error>({
    queryKey: ['admin', 'users', userId, 'sessions'],
    queryFn: async () => getAdminUserSessions(userId),
  });

  const mutation = useMutation<
    RevokeAdminUserSessionsResponse,
    AdminUserApiError | Error
  >({
    mutationFn: async () =>
      revokeAdminUserSessions(userId, { scope: 'all' }),
    onSuccess: (data) => {
      toast({
        variant: 'success',
        title: t('detail.sessions.toast.revokedTitle'),
        description: t('detail.sessions.toast.revokedDesc', {
          count: data.revoked_count,
        }),
      });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', userId] });
      queryClient.invalidateQueries({
        queryKey: ['admin', 'users', userId, 'activity'],
      });
      queryClient.invalidateQueries({
        queryKey: ['admin', 'users', userId, 'sessions'],
      });
      setConfirmOpen(false);
    },
    onError: (err) => {
      const apiErr = err as AdminUserApiError;
      toast({
        variant: 'error',
        title: t('detail.sessions.toast.failTitle'),
        description:
          apiErr.userMessage ??
          (err as Error)?.message ??
          t('detail.sessions.toast.failDesc'),
      });
    },
  });

  return (
    <div className="space-y-4" data-test-id="admin-user-sessions-section">
      <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
        <div>
          <div className="text-xs uppercase tracking-wide text-pw-text-muted">
            {t('detail.sessions.lastLogin')}
          </div>
          <div className="font-medium text-pw-text">
            {formatTimestamp(lastLoginAt)}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide text-pw-text-muted">
            {t('detail.sessions.lastIp')}
          </div>
          <div className="font-medium text-pw-text">{lastLoginIp ?? '—'}</div>
        </div>
      </div>

      {/* Active sessions list (#1058) */}
      <div>
        <div className="mb-2 text-xs uppercase tracking-wide text-pw-text-muted">
          {t('detail.sessions.activeHeading')}
        </div>
        {sessionsQuery.isLoading ? (
          <div className="text-sm text-pw-text-muted" data-test-id="admin-user-sessions-loading">
            {t('detail.sessions.loading')}
          </div>
        ) : sessionsQuery.isError ? (
          <div className="text-sm text-red-700 dark:text-red-300" data-test-id="admin-user-sessions-error">
            {t('detail.sessions.loadError')}
          </div>
        ) : (sessionsQuery.data ?? []).length === 0 ? (
          <div className="text-sm text-pw-text-muted" data-test-id="admin-user-sessions-empty">
            {t('detail.sessions.empty')}
          </div>
        ) : (
          <ul className="divide-y divide-pw-border rounded-md border border-pw-border">
            {(sessionsQuery.data ?? []).map((s) => (
              <li
                key={s.id}
                className="flex flex-col gap-0.5 px-3 py-2 text-sm"
                data-test-id={`admin-user-session-row-${s.id}`}
              >
                <span className="font-medium text-pw-text">
                  {s.user_agent || t('detail.sessions.unknownDevice')}
                </span>
                <span className="text-xs text-pw-text-muted">
                  {t('detail.sessions.lastIp')}: {s.ip ?? '—'} ·{' '}
                  {t('detail.sessions.activeAt')}:{' '}
                  {formatTimestamp(s.last_active_at)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 p-4 text-sm text-amber-900">
        <p className="font-medium">{t('detail.sessions.revokeAllHeading')}</p>
        <p className="mt-1">{t('detail.sessions.revokeAllDesc')}</p>
      </div>

      {isSelf ? (
        <div className="rounded-md border border-pw-border bg-pw-surface p-3 text-sm text-pw-text-muted">
          {t('detail.sessions.selfNote')}
        </div>
      ) : (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="destructive"
            onClick={() => setConfirmOpen(true)}
            disabled={mutation.isPending}
            data-test-id="admin-user-revoke-all-sessions-btn"
          >
            {mutation.isPending
              ? t('detail.sessions.processing')
              : t('detail.sessions.revokeAllBtn')}
          </Button>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!open) setConfirmOpen(false);
        }}
        onConfirm={() => mutation.mutate()}
        title={t('detail.sessions.confirmTitle')}
        description={
          <>
            {t('detail.sessions.confirmDescLine1')}
            <br />
            {t('detail.sessions.confirmDescLine2')}
          </>
        }
        confirmLabel={t('detail.sessions.confirmLabel')}
        cancelLabel={t('common.cancel')}
        type="destructive"
        loading={mutation.isPending}
      />
    </div>
  );
}
